import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { currentConfig } from "../config/environment";
import { getToken } from "../utils/auth";
import { tokenRefreshManager } from "./TokenRefreshManager";
import type { ApiResponse, ApiError } from "../types/auth.type";

function throwHttpError(error: unknown): never {
  if (error instanceof Error) {
    throw error;
  }
  const apiError = error as ApiError;
  throw Object.assign(
    new Error(apiError.message ?? "An unexpected error occurred"),
    apiError,
  );
}

export class HttpService {
  private readonly axiosInstance: AxiosInstance;
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: currentConfig.apiBaseUrl,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    this.setupInterceptors();
  }
  private setupInterceptors(): void {
    // Request interceptor to add auth token and check offline status
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Offline handling is done in the response interceptor
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    // Response interceptor to handle errors globally
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Handle offline errors
        if (!navigator.onLine) {
          const offlineError: ApiError = {
            message:
              "You are currently offline. Please check your internet connection.",
            status: 0,
            code: "OFFLINE",
          };
          return Promise.reject(offlineError);
        }
        const apiError: ApiError = {
          message:
            error.response?.data?.message ??
            error.message ??
            "An unexpected error occurred",
          status: error.response?.status ?? 500,
          code: error.response?.data?.code,
        };
        // Handle 401 Unauthorized — attempt token refresh + retry
        if (error.response?.status === 401) {
          return tokenRefreshManager.handleUnauthorized(error);
        }
        return Promise.reject(apiError);
      },
    );
  }
  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get<ApiResponse<T>>(
        url,
        config,
      );
      return response.data;
    } catch (error: unknown) {
      throwHttpError(error);
    }
  }
  async post<T, U = unknown>(
    url: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<T>>(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error: unknown) {
      throwHttpError(error);
    }
  }
  async put<T, U = unknown>(
    url: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put<ApiResponse<T>>(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error: unknown) {
      throwHttpError(error);
    }
  }
  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<ApiResponse<T>>(
        url,
        config,
      );
      return response.data;
    } catch (error: unknown) {
      throwHttpError(error);
    }
  }
  async patch<T, U = unknown>(
    url: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<ApiResponse<T>>(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error: unknown) {
      throwHttpError(error);
    }
  }
  /**
   * Fetch a binary resource as a Blob.
   * Uses responseType 'blob' so Axios does not JSON-parse the stream.
   */
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.axiosInstance.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data as Blob;
  }
}
// Singleton instance
export const httpService = new HttpService();
